<template>
    <Panel :title="$t('User 2FA Details')" :page="page" :total-rows="users.length" @change="changePage">
        <div id="user_table_controls">
            <div id="user_table_records">
                <div>{{ $t('Total Number Of Users') }}: {{ users.length }}</div>
            </div>
            <template v-if="editing">
                <div>
                    <label for="fImportFile"> {{ $t('CSV Import File') }}:</label>
                    <input id="fImportFile" type="file" @change="readFile" />
                </div>
                <CicButton v-if="imported.length > 0" id="user_import" @click="importUsers">
                    {{ $t('Import') }}
                </CicButton>
            </template>
        </div>

        <div style="overflow-x: auto">
            <table id="tblUserManagement" class="cic-table" style="text-align: left">
                <thead>
                    <tr>
                        <th :colspan="editing ? 6 : 5">{{ $t('Users') }}</th>
                    </tr>
                    <tr>
                        <th>{{ $t('User') }}</th>
                        <th>{{ $t('Enable 2FA') }}</th>
                        <th>{{ $t('Notification Method') }}</th>
                        <th>{{ $t('SMS Number') }}</th>
                        <th>{{ $t('Email Address') }}</th>
                        <th v-if="editing">{{ $t('Save (If Modified)') }}</th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="(user, index) in pageItems" :key="user.userId">
                        <td>{{ `${user.firstName} ${user.lastName} (${user.username})` }}</td>
                        <td>
                            <cic-checkbox
                                :id="`2fa-check-${index}`"
                                :enabled="editing"
                                :value="user.twoFA"
                                @input="(val) => handleTwoFAInput(user, val)"
                            />
                        </td>
                        <td>
                            <CicRadioGroup
                                v-if="user.twoFA"
                                :id="`2fa-type-toggle-${index}`"
                                :value="user.twoFAType"
                                :enabled="editing"
                                :options="[
                                    { value: 1, label: $t('SMS') },
                                    { value: 2, label: $t('Email') },
                                ]"
                                @input="(val) => handleInput(user, 'twoFAType', val)"
                            />
                            <span v-else>{{ $t('N/A') }}</span>
                        </td>
                        <td>
                            <CicTextbox
                                v-if="user.twoFA"
                                :id="`2fa-sms-text-${index}`"
                                :enabled="editing"
                                :value="user.twoFASMSNumber"
                                maxlength="64"
                                @input="(val) => handleInput(user, 'twoFASMSNumber', val)"
                            />
                            <span v-else>{{ $t('N/A') }}</span>
                        </td>
                        <td>
                            <CicTextbox
                                v-if="user.twoFA"
                                :id="`2fa-email-text-${index}`"
                                :enabled="editing"
                                :value="user.twoFAEmail"
                                maxlength="128"
                                @input="(val) => handleInput(user, 'twoFAEmail', val)"
                            />
                            <span v-else>{{ $t('N/A') }}</span>
                        </td>
                        <td v-if="editing">
                            <UploadFilled
                                v-if="changedUsers.includes(user.userId)"
                                :title="$t('Save')"
                                style="cursor: pointer; width: 27px; vertical-align: text-bottom"
                                @click="saveUser(user)"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </Panel>
</template>

<script>
    import { UploadFilled } from '@element-plus/icons-vue';
    import Panel from '@/components/container/Panel';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicRadioGroup from '@/components/inputs/CicRadioGroup';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import CicButton from '@/components/CicButton';
    import File from '@/mixins/File';
    import TableUtils from '@/mixins/TableUtils';
    import { load, save } from '@/utils/Http';
    import { showErrorMessage, showValidationMessage } from '@/utils/Notification';

    export default {
        name: 'UserTwoFATable',
        components: {
            CicButton,
            CicCheckbox,
            CicRadioGroup,
            CicTextbox,
            Panel,
            UploadFilled,
        },
        mixins: [TableUtils, File],
        props: {
            searchText: {
                type: String,
                required: true,
            },
        },
        data() {
            return {
                editing: false,
                page: 1,
                users: [],
                changedUsers: [],
                imported: [],
            };
        },
        computed: {
            pageItems() {
                return this.users.slice((this.page - 1) * 20, this.page * 20);
            },
        },
        watch: {
            searchText() {
                this.reload();
            },
        },
        created() {
            this.editing = this.$store.hasRight('EDIT_USER_2FA');
            this.loadTable();
        },
        methods: {
            fileOnLoad(content) {
                try {
                    this.imported = this.parseCsv(content);
                } catch (err) {
                    showErrorMessage(this.$t('CSV_PARSE_FAILED'));
                }
            },

            loadTable() {
                this.changedUsers = [];
                load('users/search', { limit: 1000, search: this.searchText }, (response) => {
                    this.users = response.users;
                });
            },

            handleInput(user, name, val) {
                user[name] = val;
                if (!this.changedUsers.includes(user.userId)) {
                    this.changedUsers.push(user.userId);
                }
            },

            handleTwoFAInput(user, val) {
                if (val || confirm(this.$t('Setting 2FA to OFF will remove 2FA SMS and Email data on save'))) {
                    this.handleInput(user, 'twoFAType', val ? 1 : 0);
                    this.handleInput(user, 'twoFA', val);
                }
            },

            reload() {
                this.imported = [];
                this.page = 1;
                this.loadTable();
            },

            importUsers() {
                save('user2fa/import', { rows: JSON.stringify(this.imported) }, this.reload);
            },

            saveUser(user) {
                if (user.twoFAType === 1 && !user.twoFASMSNumber) {
                    showValidationMessage('SMS Number is a required field');
                    return;
                }

                if (user.twoFAType === 2 && !user.twoFAEmail) {
                    showValidationMessage('Email Address is a required field');
                    return;
                }

                const params = {
                    userId: user.userId,
                    twoFA: user.twoFA ? 1 : 0,
                    twoFAType: user.twoFAType,
                    twoFASMSNumber: user.twoFASMSNumber ?? '',
                    twoFAEmail: user.twoFAEmail ?? '',
                };

                save('user/update2FA', params, () => {
                    let index = this.changedUsers.indexOf(user.userId);
                    if (index >= 0) {
                        this.changedUsers.splice(index, 1);
                    }

                    this.loadTable();
                });
            },
        },
    };
</script>
